// src/types.ts
var Color = /* @__PURE__ */ ((Color2) => {
  Color2[Color2["None"] = 0] = "None";
  Color2[Color2["Blue"] = 1] = "Blue";
  Color2[Color2["Red"] = 2] = "Red";
  Color2[Color2["Green"] = 3] = "Green";
  Color2[Color2["Yellow"] = 4] = "Yellow";
  Color2[Color2["Purple"] = 5] = "Purple";
  Color2[Color2["Evil"] = 6] = "Evil";
  return Color2;
})(Color || {});

// src/bn.ts
function bnReplacer(k, v) {
  if (typeof v === "bigint") {
    return v.toString() + "n";
  }
  return v;
}
function bnReviver(k, v) {
  if (typeof v === "string" && (v.startsWith("-") ? !isNaN(parseInt(v.charAt(1))) : !isNaN(parseInt(v.charAt(0)))) && v.charAt(v.length - 1) === "n") {
    return BigInt(v.slice(0, -1));
  }
  return v;
}

// src/grid.ts
import { zeroAddress as zeroAddress2 } from "viem";

// src/stratagems.ts
import { zeroAddress } from "viem";

// src/constants.ts
var zeroBytes32 = `0x0000000000000000000000000000000000000000000000000000000000000000`;
var zeroBytes24 = `0x000000000000000000000000000000000000000000000000`;
var EVIL_OWNER_ADDRESS = `0xFFfFfFffFFfffFFfFFfFFFFFffFFFffffFfFFFfF`;

// src/stratagems.ts
function bigIntIDToXYID(position) {
  const { x, y } = bigIntIDToXY(position);
  return "" + x + "," + y;
}
function countBits(n) {
  let count = 0;
  while (n != 0) {
    n = n & n - 1;
    count++;
  }
  return count;
}
function bigIntIDToXY(position) {
  const bn = BigInt(position);
  const x = Number(BigInt.asIntN(32, bn));
  const y = Number(BigInt.asIntN(32, bn >> 32n));
  return { x, y };
}
function bigIntIDToBigintXY(position) {
  const bn = BigInt(position);
  const x = BigInt.asIntN(32, bn);
  const y = BigInt.asIntN(32, bn >> 32n);
  return { x, y };
}
function xyToXYID(x, y) {
  return "" + x + "," + y;
}
function IDToXY(id) {
  const [x, y] = id.split(",").map(Number);
  return { x, y };
}
function xyToBigIntID(x, y) {
  const bn = (x < 0 ? 2n ** 32n + BigInt(x) : BigInt(x)) + ((y < 0 ? 2n ** 32n + BigInt(y) : BigInt(y)) << 32n);
  return bn;
}
var StratagemsContract = class {
  constructor(state, MAX_LIFE) {
    this.state = state;
    this.MAX_LIFE = MAX_LIFE;
  }
  _effectiveDelta(delta, enemyMap) {
    let effectiveDelta = delta > 0 ? 1 : -1;
    if (effectiveDelta < 0 && enemyMap == 0) {
      effectiveDelta = 1;
    }
    return effectiveDelta;
  }
  computeNewLife(lastUpdate, enemyMap, delta, life, epoch) {
    const MAX_LIFE = this.MAX_LIFE;
    const data = {
      newLife: life,
      epochUsed: lastUpdate
    };
    if (lastUpdate >= 1 && life > 0) {
      let epochDelta = epoch - lastUpdate;
      if (epochDelta > 0) {
        const effectiveDelta = this._effectiveDelta(delta, enemyMap);
        if (effectiveDelta > 0) {
          const maxEpoch = MAX_LIFE - life + Math.floor((effectiveDelta - 1) / effectiveDelta);
          if (epochDelta > maxEpoch) {
            epochDelta = maxEpoch;
          }
          life += epochDelta * effectiveDelta;
          if (life > MAX_LIFE) {
            life = MAX_LIFE;
          }
          data.newLife = life;
          data.epochUsed = epoch;
        } else if (effectiveDelta < 0) {
          const numEpochBeforeDying = life + Math.floor((-effectiveDelta - 1) / -effectiveDelta);
          if (epochDelta > numEpochBeforeDying) {
            epochDelta = numEpochBeforeDying;
          }
          const lifeLoss = epochDelta * -effectiveDelta;
          if (lifeLoss > life) {
            data.newLife = 0;
          } else {
            data.newLife = life - lifeLoss;
          }
          data.epochUsed = lastUpdate + epochDelta;
        } else {
          data.newLife = life;
          data.epochUsed = epoch;
        }
      } else {
        data.newLife = life;
        data.epochUsed = lastUpdate;
      }
    }
    return data;
  }
  getCellInMemory(position) {
    const cell = this.state.cells[position.toString()];
    return {
      lastEpochUpdate: cell?.lastEpochUpdate || 0,
      color: cell?.color || 0 /* None */,
      delta: cell?.delta || 0,
      enemyMap: cell?.enemyMap || 0,
      epochWhenTokenIsAdded: cell?.epochWhenTokenIsAdded || 0,
      life: cell?.life || 0,
      distribution: cell?.distribution || 0,
      stake: cell?.stake || 0,
      producingEpochs: cell?.producingEpochs || 0
    };
  }
  getUpdatedCell(position, epoch) {
    const updatedCell = this.getCellInMemory(position);
    let justDied = false;
    const lastUpdate = updatedCell.lastEpochUpdate;
    const delta = updatedCell.delta;
    const life = updatedCell.life;
    if (lastUpdate >= 1 && life > 0) {
      const { newLife } = this.computeNewLife(
        updatedCell.lastEpochUpdate,
        updatedCell.enemyMap,
        updatedCell.delta,
        updatedCell.life,
        epoch
      );
      updatedCell.life = newLife;
      updatedCell.lastEpochUpdate = epoch;
      justDied = newLife == 0;
    }
    const effectiveDelta = this._effectiveDelta(updatedCell.delta, updatedCell.enemyMap);
    if (effectiveDelta > 0) {
      updatedCell.producingEpochs += epoch - lastUpdate;
    }
    return { updatedCell, justDied };
  }
  ownerOf(position) {
    return this.state.owners[position.toString()] || zeroAddress;
  }
  updateCellFromNeighbor(position, cell, newLife, epoch, neighbourIndex, oldColor, newColor) {
    let due = 0;
    if (cell.life > 0 && newLife == 0) {
      cell.distribution = (cell.enemyMap << 4) + countBits(cell.enemyMap);
    }
    if ((cell.distribution >> 4 & 2 ** neighbourIndex) == 2 ** neighbourIndex) {
      due = 12 / (cell.distribution & 15);
      cell.distribution = (cell.distribution >> 4 & ~(2 ** neighbourIndex) << 4) + (cell.distribution & 15);
    }
    const oldEffectiveDelta = this._effectiveDelta(cell.delta, cell.enemyMap);
    if (oldColor != newColor) {
      if (newColor == 0 /* None */) {
        if (cell.color == oldColor) {
          cell.delta -= 1;
        } else {
          cell.delta += 1;
          cell.enemyMap = cell.enemyMap & (1 << neighbourIndex ^ 255);
        }
      } else if (cell.color == oldColor) {
        cell.enemyMap = cell.enemyMap | 1 << neighbourIndex;
        cell.delta -= 2;
      } else if (cell.color == newColor) {
        cell.delta += oldColor == 0 /* None */ ? 1 : 2;
        cell.enemyMap = cell.enemyMap & (1 << neighbourIndex ^ 255);
      } else if (oldColor == 0 /* None */) {
        cell.delta -= 1;
        cell.enemyMap = cell.enemyMap | 1 << neighbourIndex;
      }
    }
    if (oldEffectiveDelta > 0) {
      cell.producingEpochs += epoch - cell.lastEpochUpdate;
    }
    cell.lastEpochUpdate = epoch;
    cell.life = newLife;
    this.state.cells[position.toString()] = cell;
    return due;
  }
  updateCell(position, epoch, neighbourIndex, oldColor, newColor) {
    const data = {
      enemyOrFriend: 0,
      due: 0
    };
    const cell = this.getCellInMemory(position);
    const lastUpdate = cell.lastEpochUpdate;
    const color = cell.color;
    if (color != 0 /* None */) {
      data.enemyOrFriend = color == newColor ? 1 : -1;
    }
    if (lastUpdate >= 1 && color != 0 /* None */) {
      if (cell.life > 0 && lastUpdate < epoch) {
        const { newLife } = this.computeNewLife(lastUpdate, cell.enemyMap, cell.delta, cell.life, epoch);
        data.due = this.updateCellFromNeighbor(position, cell, newLife, epoch, neighbourIndex, oldColor, newColor);
      } else {
        data.due = this.updateCellFromNeighbor(position, cell, cell.life, epoch, neighbourIndex, oldColor, newColor);
      }
    }
    return data;
  }
  updateNeighbours(position, epoch, oldColor, newColor, distribution) {
    const { x, y } = bigIntIDToXY(position);
    const data = {
      newComputedDelta: 0,
      newComputedEnemyMap: 0,
      numDue: 0
    };
    {
      const upPosition = xyToBigIntID(x, y - 1);
      const { enemyOrFriend, due } = this.updateCell(upPosition, epoch, 2, oldColor, newColor);
      if (enemyOrFriend < 0) {
        data.newComputedEnemyMap = data.newComputedEnemyMap | 1;
      }
      data.numDue += due;
      data.newComputedDelta += enemyOrFriend;
    }
    {
      const leftPosition = xyToBigIntID(x - 1, y);
      const { enemyOrFriend, due } = this.updateCell(leftPosition, epoch, 3, oldColor, newColor);
      if (enemyOrFriend < 0) {
        data.newComputedEnemyMap = data.newComputedEnemyMap | 2;
      }
      data.numDue += due;
      data.newComputedDelta += enemyOrFriend;
    }
    {
      const downPosition = xyToBigIntID(x, y + 1);
      const { enemyOrFriend, due } = this.updateCell(downPosition, epoch, 0, oldColor, newColor);
      if (enemyOrFriend < 0) {
        data.newComputedEnemyMap = data.newComputedEnemyMap | 4;
      }
      data.numDue += due;
      data.newComputedDelta += enemyOrFriend;
    }
    {
      const rightPosition = xyToBigIntID(x + 1, y);
      const { enemyOrFriend, due } = this.updateCell(rightPosition, epoch, 1, oldColor, newColor);
      if (enemyOrFriend < 0) {
        data.newComputedEnemyMap = data.newComputedEnemyMap | 8;
      }
      data.numDue += due;
      data.newComputedDelta += enemyOrFriend;
    }
    return data;
  }
  propagate(move, epoch, color, distribution) {
    const data = {
      newDelta: 0,
      newEnemyMap: 0
    };
    const {
      newComputedDelta,
      newComputedEnemyMap,
      numDue
      // ownersToPay
    } = this.updateNeighbours(move.position, epoch, color, move.color, distribution);
    data.newDelta = newComputedDelta;
    data.newEnemyMap = newComputedEnemyMap;
    return data;
  }
  computeMove(player, epoch, moveAsInput) {
    const move = { ...moveAsInput };
    const MAX_LIFE = this.MAX_LIFE;
    const { updatedCell: currentState, justDied } = this.getUpdatedCell(move.position, epoch);
    let distribution = currentState.distribution;
    if (justDied) {
      distribution = (currentState.enemyMap << 4) + countBits(currentState.enemyMap);
      currentState.lastEpochUpdate = 0;
    }
    if (move.color == 0 /* None */) {
      if (currentState.life != MAX_LIFE || this.ownerOf(move.position).toLowerCase() != player.toLowerCase()) {
        return;
      }
    } else if (currentState.epochWhenTokenIsAdded == epoch) {
      if (currentState.life != 0) {
        move.color = 6 /* Evil */;
      } else {
        return;
      }
    }
    const { newDelta, newEnemyMap } = this.propagate(move, epoch, currentState.color, distribution);
    currentState.color = move.color;
    currentState.distribution = 0;
    if (!(this.ownerOf(move.position).toLowerCase() == player.toLowerCase() && currentState.life > 0)) {
      currentState.epochWhenTokenIsAdded = epoch;
    }
    if (currentState.color == 0 /* None */) {
      currentState.producingEpochs += epoch - currentState.lastEpochUpdate;
      currentState.life = 0;
      currentState.stake = 0;
      currentState.lastEpochUpdate = 0;
      currentState.delta = 0;
      currentState.enemyMap = 0;
      this.state.owners[move.position.toString()] = zeroAddress;
    } else {
      currentState.enemyMap = newEnemyMap;
      if (currentState.color == 6 /* Evil */ && currentState.life != 0) {
        currentState.stake += 1;
        if (currentState.stake > 255) {
          currentState.stake = 255;
        }
      } else {
        currentState.stake = 1;
        currentState.producingEpochs = 0;
      }
      currentState.delta = newDelta;
      currentState.life = 2;
      currentState.lastEpochUpdate = epoch;
      if (currentState.color == 6 /* Evil */) {
        this.state.owners[move.position.toString()] = EVIL_OWNER_ADDRESS;
      } else {
        this.state.owners[move.position.toString()] = player;
      }
    }
    this.state.cells[move.position.toString()] = currentState;
  }
  // ----------------------
  forceSimpleCells(epoch, cells) {
    for (const simpleCell of cells) {
      const { delta, enemyMap } = this.updateNeighbosrDelta(simpleCell.position, simpleCell.color, epoch);
      this.state.cells[simpleCell.position.toString()] = {
        lastEpochUpdate: epoch,
        epochWhenTokenIsAdded: epoch,
        color: simpleCell.color,
        life: simpleCell.life,
        delta,
        enemyMap,
        distribution: 0,
        stake: simpleCell.stake,
        producingEpochs: 0
        // THIS IS FALSE but we go with it
      };
      this.state.owners[simpleCell.position.toString()] = simpleCell.owner;
    }
    for (const simpleCell of cells) {
      const cell = this.getCellInMemory(simpleCell.position);
      const effectiveDelta = this._effectiveDelta(cell.delta, cell.enemyMap);
      let potentialLife = cell.life - effectiveDelta;
      if (potentialLife < 0) {
        potentialLife = 0;
      }
      cell.life = potentialLife;
      const newCell = {
        lastEpochUpdate: epoch - 1,
        epochWhenTokenIsAdded: epoch - 1,
        color: cell.color,
        life: cell.life,
        delta: cell.delta,
        enemyMap: cell.enemyMap,
        distribution: 0,
        stake: cell.stake,
        producingEpochs: 0
      };
      if (this._effectiveDelta(newCell.delta, newCell.enemyMap) > 0) {
        newCell.producingEpochs = 1;
      }
      this.state.cells[simpleCell.position.toString()] = newCell;
    }
  }
  updateNeighbosrDelta(center, color, epoch) {
    const { x, y } = bigIntIDToXY(center);
    const data = { delta: 0, enemyMap: 0 };
    {
      const upPosition = xyToBigIntID(x, y - 1);
      const cell = this.getCellInMemory(upPosition);
      if (cell.color != 0 /* None */) {
        const enemyOrFriend = this.isEnemyOrFriend(color, cell.color);
        if (enemyOrFriend < 0) {
          data.enemyMap = data.enemyMap | 1;
        }
        data.delta += enemyOrFriend;
        this.updateCellFromNeighbor(upPosition, cell, cell.life, epoch, 2, 0 /* None */, color);
      }
    }
    {
      const leftPosition = xyToBigIntID(x - 1, y);
      const cell = this.getCellInMemory(leftPosition);
      if (cell.color != 0 /* None */) {
        const enemyOrFriend = this.isEnemyOrFriend(color, cell.color);
        if (enemyOrFriend < 0) {
          data.enemyMap = data.enemyMap | 2;
        }
        data.delta += enemyOrFriend;
        this.updateCellFromNeighbor(leftPosition, cell, cell.life, epoch, 3, 0 /* None */, color);
      }
    }
    {
      const downPosition = xyToBigIntID(x, y + 1);
      const cell = this.getCellInMemory(downPosition);
      if (cell.color != 0 /* None */) {
        const enemyOrFriend = this.isEnemyOrFriend(color, cell.color);
        if (enemyOrFriend < 0) {
          data.enemyMap = data.enemyMap | 4;
        }
        data.delta += enemyOrFriend;
        this.updateCellFromNeighbor(downPosition, cell, cell.life, epoch, 0, 0 /* None */, color);
      }
    }
    {
      const rightPosition = xyToBigIntID(x + 1, y);
      const cell = this.getCellInMemory(rightPosition);
      if (cell.color != 0 /* None */) {
        const enemyOrFriend = this.isEnemyOrFriend(color, cell.color);
        if (enemyOrFriend < 0) {
          data.enemyMap = data.enemyMap | 8;
        }
        data.delta += enemyOrFriend;
        this.updateCellFromNeighbor(rightPosition, cell, cell.life, epoch, 1, 0 /* None */, color);
      }
    }
    return data;
  }
  poke(position, epoch) {
    const { updatedCell: currentState, justDied } = this.getUpdatedCell(position, epoch);
    let distribution = currentState.distribution;
    if (justDied) {
      distribution = (currentState.enemyMap << 4) + countBits(currentState.enemyMap);
      currentState.lastEpochUpdate = 0;
    }
    const {
      numDue
      // ownersToPay
    } = this.updateNeighbours(position, epoch, currentState.color, currentState.color, distribution);
    currentState.distribution = 0;
    this.state.cells[position.toString()] = currentState;
  }
  isEnemyOrFriend(a, b) {
    if (a != 0 /* None */ && b != 0 /* None */) {
      return a == b ? 1 : -1;
    }
    return 0;
  }
};

// src/grid.ts
function cellID(x, y) {
  if (typeof x === "object") {
    return `${x.x},${x.y}`;
  }
  return `${x},${y}`;
}
function colorCodeOf(color) {
  switch (color) {
    case 1 /* Blue */:
      return "B";
    case 2 /* Red */:
      return "R";
    case 3 /* Green */:
      return "G";
    case 4 /* Yellow */:
      return "Y";
    case 5 /* Purple */:
      return "P";
    case 6 /* Evil */:
      return "E";
    case 0 /* None */:
      return " ";
  }
}
function emptyCell(x, y) {
  return {
    x,
    y,
    owner: void 0,
    color: 0 /* None */,
    life: 0,
    stake: 0
  };
}
function parseGrid(str, forcePlayer) {
  const tmpCellMap = /* @__PURE__ */ new Map();
  let started = false;
  let indent = 0;
  let maxX = 0;
  let maxY = 0;
  let line = 0;
  let charI = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charAt(i);
    switch (char) {
      case "-":
        if (!started) {
          started = true;
          indent = charI;
        }
        break;
      case "|":
        break;
      case `
`:
        charI = -indent;
        if (started) {
          line++;
        }
        continue;
    }
    if (started && charI >= 0 && line % 3 != 0) {
      if (charI % 5 != 0) {
        const x = Math.floor(charI / 5);
        const y = Math.floor(line / 3);
        if (x > maxX) {
          maxX = x;
        }
        if (y > maxY) {
          maxY = y;
        }
        let cell = tmpCellMap.get(cellID(x, y));
        if (!cell) {
          cell = {
            empty: true,
            playerAsString: "",
            ...emptyCell(x, y)
          };
          tmpCellMap.set(cellID(x, y), cell);
        }
        if (charI % 5 == 1) {
          if (char === "+") {
            cell.addition = true;
            cell.empty = false;
          }
        } else {
          if (line % 3 == 1) {
            switch (char) {
              case " ":
                break;
              case "B":
                cell.color = 1 /* Blue */;
                cell.empty = false;
                break;
              case "R":
                cell.color = 2 /* Red */;
                cell.empty = false;
                break;
              case "G":
                cell.color = 3 /* Green */;
                cell.empty = false;
                break;
              case "Y":
                cell.color = 4 /* Yellow */;
                cell.empty = false;
                break;
              case "P":
                cell.color = 5 /* Purple */;
                cell.empty = false;
                break;
              case "E":
                cell.color = 6 /* Evil */;
                cell.empty = false;
                break;
              case "0":
                cell.life = 0;
                break;
              case "1":
                cell.life = 1;
                break;
              case "2":
                cell.life = 2;
                break;
              case "3":
                cell.life = 3;
                break;
              case "4":
                cell.life = 4;
                break;
              case "5":
                cell.life = 5;
                break;
              case "6":
                cell.life = 6;
                break;
              case "7":
                cell.life = 7;
                break;
              case "8":
                cell.life = 8;
                break;
              case "9":
                cell.life = 9;
                break;
            }
          } else {
            if (char != " ") {
              cell.playerAsString += char;
            }
          }
        }
      }
    }
    charI++;
  }
  const actions = [];
  const cells = [];
  for (const cell of tmpCellMap.values()) {
    if (!cell.empty) {
      const { empty, playerAsString, addition, ...onlyCell } = cell;
      if (playerAsString !== "") {
        const ownerNumber = parseInt(playerAsString);
        onlyCell.owner = ownerNumber;
        if (ownerNumber < 0) {
          onlyCell.stake = -ownerNumber;
        } else {
          onlyCell.stake = 1;
        }
      }
      if (forcePlayer) {
        onlyCell.owner = forcePlayer;
      }
      if (addition) {
        if (!onlyCell.owner) {
          throw new Error(`addition (+) need an owner`);
        }
        let color;
        if (onlyCell.color > 5) {
          throw new Error(`invalid color value for action: ${onlyCell.color}`);
        }
        color = onlyCell.color;
        actions.push({
          color,
          owner: onlyCell.owner,
          x: onlyCell.x,
          y: onlyCell.y
        });
      } else {
        cells.push(onlyCell);
      }
    }
  }
  if (actions.length > 0) {
    return { cells, width: maxX + 1, height: maxY + 1, actions };
  }
  return { cells, width: maxX + 1, height: maxY + 1 };
}
function renderGrid(grid) {
  const cellMap = /* @__PURE__ */ new Map();
  for (const cell of grid.cells) {
    cellMap.set(cellID(cell), cell);
  }
  let str = "";
  for (let x = 0; x < grid.width; x++) {
    str += "-----";
  }
  str += `
`;
  for (let y = 0; y < grid.height; y++) {
    for (let r = 0; r < 3; r++) {
      if (r != 2) {
        str += "|";
      }
      for (let x = 0; x < grid.width; x++) {
        const cell = cellMap.get(cellID(x, y));
        if (r == 2) {
          str += "-----";
        } else {
          if (cell?.owner != void 0) {
            if (r == 0) {
              str += " ";
              str += colorCodeOf(cell.color);
              str += cell.life.toString();
              str += " ";
            } else {
              if (cell.owner !== void 0) {
                let ownerNumber = cell.owner;
                if (cell.owner === 0) {
                  ownerNumber = -cell.stake;
                }
                str += ` ${ownerNumber.toString().padStart(2, "0")} `;
              } else {
                str += "    ";
              }
            }
          } else {
            str += "    ";
          }
          str += "|";
        }
      }
      str += `
`;
    }
  }
  return str;
}
function toContractSimpleCell(accounts) {
  return (cell) => ({
    color: cell.color,
    life: cell.life,
    owner: cell.owner ? cell.owner >= 0 ? accounts[cell.owner] : accounts[0] : zeroAddress2,
    position: xyToBigIntID(cell.x, cell.y),
    stake: cell.stake
  });
}
function fromContractFullCellToCell(accounts) {
  return ({ cell, id }) => {
    const { x, y } = bigIntIDToXY(id);
    const accountIndex = accounts.indexOf(cell.owner);
    const processedCell = {
      x,
      y,
      lastEpochUpdate: cell.lastEpochUpdate,
      epochWhenTokenIsAdded: cell.epochWhenTokenIsAdded,
      color: cell.color,
      life: cell.life,
      delta: cell.delta,
      enemyMap: cell.enemyMap,
      owner: cell.owner == zeroAddress2 ? void 0 : accountIndex >= 0 ? accountIndex : cell.owner.toLowerCase() === EVIL_OWNER_ADDRESS.toLowerCase() ? -cell.stake : void 0,
      stake: cell.stake
    };
    return processedCell;
  };
}

// src/commitment.ts
import { encodeAbiParameters, keccak256 } from "viem";
import * as crypto from "crypto";
function randomSecret() {
  return `0x` + [...crypto.getRandomValues(new Uint8Array(32))].map((m) => ("0" + m.toString(16)).slice(-2)).join("");
}
function prepareCommitment(moves, secret) {
  const commitmentHash = keccak256(
    encodeAbiParameters(
      [
        { type: "bytes32", name: "secret" },
        {
          components: [
            {
              name: "position",
              type: "uint64"
            },
            {
              name: "color",
              type: "uint8"
            }
          ],
          name: "moves",
          type: "tuple[]"
        }
      ],
      [secret, moves]
    )
  ).slice(0, 50);
  return { secret, hash: commitmentHash, moves };
}
export {
  Color,
  EVIL_OWNER_ADDRESS,
  IDToXY,
  StratagemsContract,
  bigIntIDToBigintXY,
  bigIntIDToXY,
  bigIntIDToXYID,
  bnReplacer,
  bnReviver,
  countBits,
  fromContractFullCellToCell,
  parseGrid,
  prepareCommitment,
  randomSecret,
  renderGrid,
  toContractSimpleCell,
  xyToBigIntID,
  xyToXYID,
  zeroBytes24,
  zeroBytes32
};
//# sourceMappingURL=index.js.map